import SuperFetch, { postForBlob } from 'library/helpers/superFetch';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import { saveBlob } from 'library/helpers/saveBlob';
import { all, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { dayPassesActions } from './slice';

// Backend mount is /v5/:orgID/daypass (the App Server proxy) — SuperFetch
// prepends /v5/{orgID}. The proxy returns the day-pass admin envelope: lists as
// { items, total }, single resources / mutations as the object.
const BASE = '/daypass';

// resource key → GET path + whether the response is a list ({items}) or object.
const RESOURCE_PATHS = {
	org: { path: '/org', list: false },
	passTypes: { path: '/pass-types', list: true },
	capacity: { path: '/booking-resources', list: true },
	sites: { path: '/sites', list: true },
	devices: { path: '/devices', list: true },
	config: { path: '/org/config', list: false },
	branding: { path: '/org/branding', list: false }
};

const errMessage = (res, fallback) => res.error?.message || res.error || fallback;

// 🔥 Generic list/object load for one resource.
export function* loadResource({ payload }) {
	const { resource } = payload;
	const spec = RESOURCE_PATHS[resource];
	try {
		if (!spec) throw Error(`Unknown day-pass resource: ${resource}`);
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${BASE}${spec.path}`, {}, payload.query || {}));
		if (res.statusCode === 200) {
			const data = spec.list ? (res.data?.items ?? []) : (res.data ?? null);
			yield put(dayPassesActions.loadResourceSuccess({ orgID, resource, data }));
		} else {
			throw Error(errMessage(res, `Failed to load ${resource}.`));
		}
	} catch (error) {
		yield put(dayPassesActions.loadResourceError(error));
	}
}

// 🔥 Sales log + metrics (one action loads both; each has its own endpoint).
export function* loadPasses({ payload = {} }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const [listRes, metricsRes] = yield all([
			call(() => SuperFetch.get(`${BASE}/passes`, {}, payload.query || {})),
			call(() => SuperFetch.get(`${BASE}/passes/metrics`))
		]);
		if (listRes.statusCode !== 200) throw Error(errMessage(listRes, 'Failed to load passes.'));
		yield put(
			dayPassesActions.loadPassesSuccess({
				orgID,
				passes: listRes.data?.items ?? [],
				metrics: metricsRes.statusCode === 200 ? metricsRes.data : undefined
			})
		);
	} catch (error) {
		yield put(dayPassesActions.loadPassesError(error));
	}
}

/**
 * 🔥 Shared mutation for every drawer/toggle. The action carries the HTTP verb,
 * the sub-path, an optional body, a success toast, and the resource(s) to
 * refetch — so the reducers only track the lifecycle flags and every write
 * goes through one code path.
 *
 * mutate({ method, path, body?, successMessage?, refetch?: [resourceKeys] })
 */
export function* mutate({ payload }) {
	const { method, path, body, successMessage, refetch = [] } = payload;
	try {
		const verb = method.toLowerCase();
		const res = yield call(() =>
			verb === 'delete' ? SuperFetch.delete(`${BASE}${path}`) : SuperFetch[verb](`${BASE}${path}`, body || {})
		);
		if (res.statusCode === 200 || res.statusCode === 201) {
			if (successMessage) Notification('success', successMessage);
			yield put(dayPassesActions.mutateSuccess());
			for (const resource of refetch) {
				yield put(dayPassesActions.loadResource({ resource }));
			}
		} else {
			throw Error(errMessage(res, 'The change could not be saved.'));
		}
	} catch (error) {
		yield put(dayPassesActions.mutateError(error));
		Notification('error', error.message || 'The change could not be saved.');
	}
}

// Day-Pass Fold platform domain — the NEW platform-native model, distinct from the
// Azure proxy BASE above. SuperFetch prepends /v5/{orgID}; this domain returns the
// standard v5 envelope ({ statusCode, data }), so list reads take res.data directly
// (no { items } unwrap).
const FOLD_BASE = '/day-pass';

// 🔥 Load a bookable resource's platform pass types (GET /day-pass/passTypes?resourceID=).
export function* loadFoldPassTypes({ payload }) {
	const { resourceID } = payload;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${FOLD_BASE}/passTypes`, {}, { resourceID }));
		if (res.statusCode === 200) {
			yield put(dayPassesActions.loadFoldPassTypesSuccess({ orgID, resourceID, data: res.data ?? [] }));
		} else {
			throw Error(errMessage(res, 'Failed to load passes for this resource.'));
		}
	} catch (error) {
		yield put(dayPassesActions.loadFoldPassTypesError(error));
	}
}

// 🔥 Load ALL of the org's platform pass types (GET /day-pass/passTypes, no resourceID) — the
// Pass-types tab list. Each row carries resourceName + siteID from the org-wide endpoint.
export function* loadFoldPassTypesAll() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${FOLD_BASE}/passTypes`));
		if (res.statusCode === 200) {
			yield put(dayPassesActions.loadFoldPassTypesAllSuccess({ orgID, data: res.data ?? [] }));
		} else {
			throw Error(errMessage(res, 'Failed to load pass types.'));
		}
	} catch (error) {
		yield put(dayPassesActions.loadFoldPassTypesAllError(error));
	}
}

// 🔥 Load every sale (GET /day-pass/sales) — the Sales-log tab.
export function* loadFoldSales() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${FOLD_BASE}/sales`));
		if (res.statusCode === 200) {
			yield put(dayPassesActions.loadFoldSalesSuccess({ orgID, data: res.data ?? [] }));
		} else {
			throw Error(errMessage(res, 'Failed to load sales.'));
		}
	} catch (error) {
		yield put(dayPassesActions.loadFoldSalesError(error));
	}
}

// A statement period id is the period's local start date. Anything else never reaches a URL: the id
// comes from the address bar (?period=), and an unchecked value would let a crafted link steer the
// request to another path on the API (audit round 1, B-18).
export const PERIOD_ID = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const periodPath = (periodId) => {
	if (!PERIOD_ID.test(periodId || '')) throw Error('That statement period does not exist.');
	return `${FOLD_BASE}/statements/${encodeURIComponent(periodId)}`;
};

// 🔥 Settlement statements — the period list (GET /day-pass/statements).
export function* loadFoldStatements() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${FOLD_BASE}/statements`));
		if (res.statusCode === 200) {
			yield put(dayPassesActions.loadFoldStatementsSuccess({ orgID, data: res.data ?? [] }));
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load statements.'));
		}
	} catch (error) {
		yield put(dayPassesActions.loadFoldStatementsError(error.message || String(error)));
	}
}

// 🔥 One statement (GET /day-pass/statements/:periodId) — frozen snapshot or live, as the API decides.
export function* loadFoldStatement({ payload }) {
	const { periodId } = payload;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(periodPath(periodId)));
		if (res.statusCode === 200) {
			yield put(dayPassesActions.loadFoldStatementSuccess({ orgID, periodId, data: res.data }));
		} else {
			throw Error(apiFailureMessage(res, 'Failed to load the statement.'));
		}
	} catch (error) {
		yield put(dayPassesActions.loadFoldStatementError({ periodId, message: error.message || String(error) }));
	}
}

// 🔥 Freeze a period (POST /day-pass/statements/:periodId/remit). Staff only — the server refuses anyone else.
export function* remitFoldStatement({ payload }) {
	const { periodId, reference, date, expectedNetCents, expectedLineCount } = payload;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() =>
			SuperFetch.post(`${periodPath(periodId)}/remit`, { reference, date, expectedNetCents, expectedLineCount })
		);
		if (res.statusCode === 200 || res.statusCode === 201) {
			yield put(dayPassesActions.remitFoldStatementSuccess({ orgID, periodId, data: res.data }));
			Notification('success', `Statement ${res.data?.statementNumber || ''} marked remitted.`);
			yield put(dayPassesActions.loadFoldStatements());
		} else {
			throw Error(apiFailureMessage(res, 'The statement could not be marked remitted.'));
		}
	} catch (error) {
		const message = error.message || String(error);
		yield put(dayPassesActions.remitFoldStatementError(message));
		Notification('error', message);
	}
}

// 🔥 Download the statement as CSV (POST /day-pass/statements/:periodId/export answers with the file).
export function* exportFoldStatement({ payload }) {
	const { periodId, filename } = payload;
	try {
		const res = yield call(postForBlob, `${periodPath(periodId)}/export`, {});
		if (res.statusCode === 200 && res.blob) {
			yield call(saveBlob, res.blob, res.filename || filename || `statement-${periodId}.csv`);
		} else {
			throw Error(apiFailureMessage(res, 'The statement could not be exported.'));
		}
	} catch (error) {
		Notification('error', error.message || String(error));
	} finally {
		yield put(dayPassesActions.exportFoldStatementDone());
	}
}

/**
 * 🔥 Platform pass-type mutation (create/update/delete on /day-pass). Mirrors `mutate`
 * but targets FOLD_BASE and refetches on success — a single resource's list
 * (reloadResourceID, create wizard) and/or the whole org list (reloadAllForOrg, Pass-types tab).
 *
 * foldMutate({ method, path, body?, successMessage?, reloadResourceID?, reloadAllForOrg? })
 */
export function* foldMutate({ payload }) {
	const { method, path, body, successMessage, reloadResourceID, reloadAllForOrg } = payload;
	try {
		const verb = method.toLowerCase();
		const res = yield call(() =>
			verb === 'delete'
				? SuperFetch.delete(`${FOLD_BASE}${path}`)
				: SuperFetch[verb](`${FOLD_BASE}${path}`, body || {})
		);
		if (res.statusCode === 200 || res.statusCode === 201) {
			if (successMessage) Notification('success', successMessage);
			yield put(dayPassesActions.foldMutateSuccess());
			if (reloadResourceID) yield put(dayPassesActions.loadFoldPassTypes({ resourceID: reloadResourceID }));
			if (reloadAllForOrg) yield put(dayPassesActions.loadFoldPassTypesAll());
		} else {
			throw Error(errMessage(res, 'The change could not be saved.'));
		}
	} catch (error) {
		yield put(dayPassesActions.foldMutateError(error));
		Notification('error', error.message || 'The change could not be saved.');
	}
}

export default function* dayPassesSagas() {
	yield takeEvery(dayPassesActions.loadResource.type, loadResource);
	yield takeLatest(dayPassesActions.loadPasses.type, loadPasses);
	// takeEvery, not takeLatest: independent cards (e.g. Settings' regional +
	// branding saves) must each complete — takeLatest would cancel the first
	// after its request already fired, skipping its toast + refetch.
	yield takeEvery(dayPassesActions.mutate.type, mutate);
	yield takeEvery(dayPassesActions.loadFoldPassTypes.type, loadFoldPassTypes);
	yield takeLatest(dayPassesActions.loadFoldPassTypesAll.type, loadFoldPassTypesAll);
	yield takeLatest(dayPassesActions.loadFoldSales.type, loadFoldSales);
	yield takeLatest(dayPassesActions.loadFoldStatements.type, loadFoldStatements);
	yield takeLatest(dayPassesActions.loadFoldStatement.type, loadFoldStatement);
	yield takeLatest(dayPassesActions.remitFoldStatement.type, remitFoldStatement);
	yield takeLatest(dayPassesActions.exportFoldStatement.type, exportFoldStatement);
	yield takeEvery(dayPassesActions.foldMutate.type, foldMutate);
}
